import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FaBullseye, FaInfoCircle } from "react-icons/fa";

import { publishServiceOrder } from "../../utils/serviceOrderSyncApi";
import { createPointOfInterestServiceOrder } from "./fieldAppApi";
import FieldAppPoiMapModal from "./FieldAppPoiMapModal";

import "./FieldAppCalibrationPanel.css";
import "./FieldAppPointsOfInterestPanel.css";
import "./FieldAppPoiMapModal.css";

const KIND_LABELS = {
  toco: "Toco",
  pedra: "Pedra",
  valeta: "Valeta",
  besta: "Valeta",
  buraco: "Buraco",
  outro: "Outro",
};

function readPayload(row) {
  return row && (row.payloadData || row.payload_data || row.payload) || {};
}

function firstDefined() {
  for (let i = 0; i < arguments.length; i += 1) {
    if (arguments[i] != null) return arguments[i];
  }
  return null;
}

function readCoordinates(payload) {
  const nested = payload.localizacao || {};
  const lat = firstDefined(payload.latitude, payload.lat, nested.latitude);
  const lng = firstDefined(payload.longitude, payload.lng, nested.longitude);
  const latitude = lat == null ? null : Number(lat);
  const longitude = lng == null ? null : Number(lng);
  return {
    lat: Number.isFinite(latitude) ? latitude : null,
    lng: Number.isFinite(longitude) ? longitude : null,
  };
}

function readPoint(row) {
  const payload = readPayload(row);
  const coords = readCoordinates(payload);
  const kind = payload.tipo_ponto || payload.kind || "outro";
  const label =
    payload.rotulo ||
    payload.valor_principal ||
    KIND_LABELS[kind] ||
    "Ponto de interesse";
  return {
    uuid: row.uuid,
    kind,
    label,
    note: payload.observacao || payload.note || "",
    lat: coords.lat,
    lng: coords.lng,
    operatorName: row.operatorName || "—",
    receivedAt: row.received_at,
  };
}

function formatCoord(value) {
  if (value == null) return "—";
  return Number(value).toFixed(6);
}

function toLocalIso(date) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds()
  )}`;
}

export default function FieldAppPointsOfInterestPanel({
  farmId,
  rows,
  operators,
  formatDateTime,
  onOpenDetail,
  t,
}) {
  const [selectedUuids, setSelectedUuids] = useState([]);
  const [operatorId, setOperatorId] = useState("");
  const [sending, setSending] = useState(false);
  const [mapPoints, setMapPoints] = useState(null);

  const points = useMemo(() => (rows || []).map(readPoint), [rows]);

  const toggleUuid = (uuid) => {
    setSelectedUuids((current) =>
      current.includes(uuid)
        ? current.filter((item) => item !== uuid)
        : [...current, uuid]
    );
  };

  const selectedPoints = points.filter((point) =>
    selectedUuids.includes(point.uuid)
  );

  const handleSend = async () => {
    if (!selectedPoints.length) {
      toast.warn(t("Selecione ao menos um ponto de interesse."));
      return;
    }
    if (!operatorId) {
      toast.warn(t("Escolha o operador que deve receber os pontos no app."));
      return;
    }
    const missingCoords = selectedPoints.filter(
      (point) => point.lat == null || point.lng == null
    );
    if (missingCoords.length) {
      toast.warn(t("Alguns pontos selecionados não têm GPS. Desmarque-os."));
      return;
    }

    const now = new Date();
    const end = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const labels = selectedPoints.map((point) => point.label).join(", ");

    setSending(true);
    try {
      const created = await createPointOfInterestServiceOrder(farmId, {
        start_date: toLocalIso(now),
        end_date: toLocalIso(end),
        visible_at: toLocalIso(now),
        description: `${t("Pontos de interesse")}: ${labels}`,
        operator_ids: [parseInt(operatorId, 10)],
        service_order_plan: {
          points_of_interest: selectedPoints.map((point) => ({
            kind: point.kind,
            label: point.label,
            note: point.note,
            lat: point.lat,
            lng: point.lng,
            source_record_uuid: point.uuid,
          })),
        },
      });

      if (created && created.id) {
        try {
          await publishServiceOrder(created.id);
        } catch (publishError) {
          toast.warn(
            t(
              "Ordem criada, mas não foi possível republicar no app. O envio inicial já pode ter ocorrido."
            )
          );
        }
      }

      toast.success(
        t("Pontos enviados ao operador. Eles abrem no app como ordem de serviço.")
      );
      setSelectedUuids([]);
    } catch (error) {
      const detail =
        error &&
        error.response &&
        error.response.data &&
        (error.response.data.detail ||
          Object.values(error.response.data).flat().join(" "));
      toast.error(detail || t("Não foi possível enviar os pontos ao operador."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="field-app-poi">
      <div className="field-app-calibration-help" role="note">
        <FaInfoCircle className="field-app-calibration-help-icon" aria-hidden />
        <div>
          <strong>{t("Pontos marcados no campo")}</strong>
          <p>
            {t(
              "O operador marca toco, pedra, valeta, buraco ou um texto livre no tablet. Selecione os pontos e envie para o app de um operador específico, como uma ordem de serviço."
            )}
          </p>
        </div>
      </div>

      <div className="field-app-poi-dispatch">
        <label>
          <span>{t("Enviar para o operador")}</span>
          <select
            value={operatorId}
            onChange={(event) => setOperatorId(event.target.value)}
          >
            <option value="">{t("Selecione o operador")}</option>
            {(operators || []).map((operator) => (
              <option key={operator.id} value={operator.id}>
                {operator.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="btn btn-default btn-sm"
          disabled={!points.some((point) => point.lat != null)}
          onClick={() =>
            setMapPoints(points.filter((point) => point.lat != null))
          }
        >
          {t("Ver todos no mapa")}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={sending}
          onClick={handleSend}
        >
          <FaBullseye aria-hidden />{" "}
          {sending
            ? t("Enviando…")
            : t("Enviar pontos selecionados ao app")}
        </button>
      </div>

      <div className="table-responsive">
        <table className="table table-striped jambo_table">
          <thead>
            <tr>
              <th></th>
              <th>{t("Marcado em")}</th>
              <th>{t("Tipo")}</th>
              <th>{t("Operador")}</th>
              <th>{t("GPS")}</th>
              <th>{t("Observação")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {points.length === 0 ? (
              <tr>
                <td colSpan="7">
                  {t("Nenhum ponto de interesse neste período.")}
                </td>
              </tr>
            ) : (
              points.map((point, index) => (
                <tr key={point.uuid || index}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedUuids.includes(point.uuid)}
                      onChange={() => toggleUuid(point.uuid)}
                      aria-label={t("Selecionar ponto")}
                    />
                  </td>
                  <td>{formatDateTime(point.receivedAt)}</td>
                  <td>{point.label}</td>
                  <td>{point.operatorName}</td>
                  <td>
                    {point.lat == null ? (
                      "—"
                    ) : (
                      <button
                        type="button"
                        className="field-app-poi-coord-btn"
                        onClick={() => setMapPoints([point])}
                      >
                        {formatCoord(point.lat)}, {formatCoord(point.lng)}
                      </button>
                    )}
                  </td>
                  <td>{point.note || "—"}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-default"
                      onClick={() => onOpenDetail(rows[index])}
                    >
                      {t("Detalhe")}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {mapPoints ? (
        <FieldAppPoiMapModal
          points={mapPoints}
          title={
            mapPoints.length === 1
              ? mapPoints[0].label
              : t("Pontos de interesse")
          }
          onClose={() => setMapPoints(null)}
          t={t}
        />
      ) : null}
    </div>
  );
}
